import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    fetchCommissionCases, createCommissionCase, updateCommissionCase, deleteCommissionCase,
    fetchCommissionOpeners, fetchCommissionUsers,
} from '../../api';
import DataGrid from '../common/DataGrid';
import Modal from '../marketing/Modal';
import { actionsColumn, formatDate, formatMoney, moneyColumn, toISODate } from '../marketing/shared';
import { caseColumn, formatDay, formatPaidAt, formatPct, splitCaps } from './shared';
import '../reports/Reports.css';
import '../marketing/Marketing.css';
import './Commissions.css';

// Commission splits, entered a case at a time. A case has one or more
// invoices, each with its commissionable amount (usually the invoice total)
// and any number of split agents, each with a percentage of every payment
// (their commissionable) and a commission rate on that. The settlement
// officer on each payment gets the percentage that invoice's split agents
// leave, at the officer rate (usually 20%). So a $495 initial invoice can go
// wholly to one salesperson and a later $6,256 invoice be split.
//
// The payment report doesn't say which invoice a payment pays, so payments
// fill a case's invoices in order: the first until it's paid off, then the
// next. When that's wrong (the client paid two invoices at once), a payment
// can be moved to a specific invoice in the case dialog.
//
// A split agent can be an existing agent, an app user who isn't one yet, or a
// new name typed in the form; the server adds them in the last two cases.
// Opened as /commissions/splits?case=123&opener=Name (the shortcut on the
// Payment Imports page), it opens that case, new or existing, with the
// report's opener added as a split agent on its last invoice.

const DEFAULT_PCT = '10';
const DEFAULT_RATE = '20';

// An agent row's opener field is the picker's value: "o:<agent id>",
// "u:<app user id>", or NEW_OPENER (then newOpener holds the name).
const NEW_OPENER = 'new';

// A payment's invoice picker value when it fills the invoices in order.
const IN_ORDER = '';

// openerBody turns an agent row's picker value into the split's person fields.
function openerBody(agent) {
    if (agent.opener === NEW_OPENER) return { opener_name: agent.newOpener.trim() };
    const [kind, id] = agent.opener.split(':');
    return kind === 'u' ? { user_id: Number(id) } : { opener_id: Number(id) };
}

const sameName = (a, b) => a.trim().toLowerCase() === b.trim().toLowerCase();

const parseAmount = (value) => Number(String(value).replace(/[$,]/g, ''));

// Percentages summed in basis points, so 33.33 + 66.67 is exactly 100.
const totalPct = (agents) => agents.reduce((sum, a) => sum + Math.round(Number(a.pct || 0) * 100), 0) / 100;

const agentLabel = (s) => (!s.opener_active && s.opener_end_date
    ? `${s.opener_name} (left ${formatDay(s.opener_end_date)})`
    : s.opener_name);

// invoiceName is how an invoice reads everywhere: "Invoice 2 · Additional
// Services", or just "Invoice 2".
const invoiceName = (position, label) => `Invoice ${position}${label ? ` · ${label}` : ''}`;

function EarnedProgress({ data, node }) {
    if (!data || (node && node.rowPinned) || !data.cap) return null;
    const ratio = Math.min(1, data.earned / data.cap);
    const done = ratio >= 1;
    return (
        <div className="com__progress" title={`${formatMoney(data.earned)} of ${formatMoney(data.cap)}`}>
            <div className="com__progress-track">
                <div
                    className={`com__progress-fill ${done ? 'com__progress-fill--done' : ''}`}
                    style={{ width: `${Math.max(0, ratio) * 100}%` }}
                />
            </div>
            <span className="com__progress-label">{Math.round(ratio * 100)}%</span>
        </div>
    );
}

// personRows flattens cases into one row per person per invoice: each split
// agent, then the settlement officer's share (left out when the agents take
// it all).
function personRows(cases) {
    const rows = [];
    cases.forEach((c) => {
        c.invoices.forEach((inv) => {
            const base = {
                kase: c,
                case_number: c.case_number,
                client_name: c.client_name,
                invoice: invoiceName(inv.position, inv.label),
                invoice_amount: inv.commissionable_amount,
            };
            inv.splits.forEach((s) => rows.push({
                ...base,
                id: `s${s.id}`,
                person: agentLabel(s),
                role: 'Split agent',
                share: s.split_pct,
                rate: s.commission_pct,
                commissionable_cap: s.commissionable_cap,
                commissionable: s.commissionable,
                cap: s.cap,
                earned: s.earned,
                remaining: s.remaining,
                forfeited: s.forfeited,
            }));
            if (inv.officer_pct > 0) {
                rows.push({
                    ...base,
                    id: `o${inv.id}`,
                    person: c.officers.length ? c.officers.join(', ') : 'No payments yet',
                    role: 'Settlement officer',
                    share: inv.officer_pct,
                    rate: inv.officer_commission_pct,
                    commissionable_cap: inv.officer_commissionable_cap,
                    commissionable: inv.officer_commissionable,
                    cap: inv.officer_cap,
                    earned: inv.officer_earned,
                    remaining: null,
                    forfeited: inv.officer_forfeited,
                });
            }
        });
    });
    return rows;
}

export default function Splits() {
    const [cases, setCases] = useState([]);
    const [openers, setOpeners] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [view, setView] = useState('case');

    // The open dialog: { mode: 'create' | 'edit' | 'delete', kase }.
    const [dialog, setDialog] = useState(null);
    // form: { caseNumber, notes, invoices: [{ key, id, label, amount,
    // officerRate, agents: [...] }], pins: { [payment id]: invoice key or
    // IN_ORDER } }.
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [dialogError, setDialogError] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const nextKey = useRef(1);

    const newAgent = useCallback((fields = {}) => ({
        key: nextKey.current++, opener: '', newOpener: '', pct: DEFAULT_PCT, rate: DEFAULT_RATE, ...fields,
    }), []);
    const newInvoice = useCallback((fields = {}) => ({
        key: nextKey.current++, id: 0, label: '', amount: '', officerRate: DEFAULT_RATE, agents: [], ...fields,
    }), []);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [c, o, u] = await Promise.all([fetchCommissionCases(), fetchCommissionOpeners(), fetchCommissionUsers()]);
            setCases(c || []);
            setOpeners(o || []);
            setUsers(u || []);
        } catch (err) {
            setError(err.message);
            setCases([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const openCreate = useCallback((prefill = {}) => {
        setForm({
            caseNumber: '',
            notes: '',
            invoices: [newInvoice({ agents: [newAgent()] })],
            pins: {},
            ...prefill,
        });
        setDialogError(null);
        setDialog({ mode: 'create' });
    }, [newAgent, newInvoice]);

    // openEdit fills the form from a saved case; extraAgent (from the
    // Imports shortcut) joins its last invoice unless already on it.
    const openEdit = useCallback((kase, extraAgent) => {
        const invoices = kase.invoices.map((inv) => newInvoice({
            id: inv.id,
            label: inv.label || '',
            amount: String(inv.commissionable_amount),
            officerRate: String(inv.officer_commission_pct),
            agents: inv.splits.map((s) => newAgent({
                opener: `o:${s.opener_id}`,
                pct: String(s.split_pct),
                rate: String(s.commission_pct),
            })),
        }));
        if (extraAgent && invoices.length) {
            const last = invoices[invoices.length - 1];
            if (!last.agents.some((a) => a.opener && a.opener === extraAgent.opener)) last.agents.push(newAgent(extraAgent));
        }
        const keyById = new Map(invoices.map((inv) => [inv.id, inv.key]));
        const pins = {};
        kase.payment_list.forEach((p) => {
            pins[p.id] = p.pinned_invoice_id ? keyById.get(p.pinned_invoice_id) ?? IN_ORDER : IN_ORDER;
        });
        setForm({ caseNumber: kase.case_number, notes: kase.notes || '', invoices, pins });
        setDialogError(null);
        setDialog({ mode: 'edit', kase, pins });
    }, [newAgent, newInvoice]);

    // The Payment Imports shortcut: open ?case= (editing it if it's already
    // entered) with the person named ?opener= as a split agent: an agent
    // first, then an app user, else typed in as someone new.
    useEffect(() => {
        const caseNumber = searchParams.get('case');
        if (!caseNumber || loading) return;
        const name = (searchParams.get('opener') || '').trim();
        let agent = null;
        if (name) {
            const o = openers.find((x) => sameName(x.name, name));
            const u = !o && users.find((x) => !x.opener_id && sameName(x.name, name));
            if (o) agent = { opener: `o:${o.id}` };
            else if (u) agent = { opener: `u:${u.id}` };
            else agent = { opener: NEW_OPENER, newOpener: name };
        }
        const existing = cases.find((c) => c.case_number === caseNumber);
        if (existing) openEdit(existing, agent);
        else openCreate({ caseNumber, invoices: [newInvoice({ agents: [newAgent(agent || {})] })] });
        setSearchParams({}, { replace: true });
    }, [searchParams, setSearchParams, loading, cases, openers, users, openCreate, openEdit, newAgent, newInvoice]);

    const openDelete = useCallback((kase) => {
        setDialogError(null);
        setDialog({ mode: 'delete', kase });
    }, []);

    const closeDialog = () => { setDialog(null); setDialogError(null); };

    // Form updaters.
    const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
    const updateInvoice = (invKey, change) => setForm((f) => ({
        ...f,
        invoices: f.invoices.map((inv) => (inv.key === invKey ? { ...inv, ...change(inv) } : inv)),
    }));
    const setInvoiceField = (invKey, field) => (e) => {
        const value = e.target.value;
        updateInvoice(invKey, () => ({ [field]: value }));
    };
    const setAgentField = (invKey, agentKey, field) => (e) => {
        const value = e.target.value;
        updateInvoice(invKey, (inv) => ({ agents: inv.agents.map((a) => (a.key === agentKey ? { ...a, [field]: value } : a)) }));
    };
    const addAgent = (invKey) => updateInvoice(invKey, (inv) => ({ agents: [...inv.agents, newAgent()] }));
    const removeAgent = (invKey, agentKey) => updateInvoice(invKey, (inv) => ({ agents: inv.agents.filter((a) => a.key !== agentKey) }));
    const addInvoice = () => setForm((f) => ({ ...f, invoices: [...f.invoices, newInvoice({ agents: [newAgent()] })] }));
    const removeInvoice = (invKey) => setForm((f) => ({
        ...f,
        invoices: f.invoices.filter((inv) => inv.key !== invKey),
        // Payments pinned to it go back to filling the invoices in order.
        pins: Object.fromEntries(Object.entries(f.pins).map(([id, k]) => [id, k === invKey ? IN_ORDER : k])),
    }));
    const moveInvoice = (invKey, by) => setForm((f) => {
        const invoices = [...f.invoices];
        const i = invoices.findIndex((inv) => inv.key === invKey);
        const j = i + by;
        if (i < 0 || j < 0 || j >= invoices.length) return f;
        [invoices[i], invoices[j]] = [invoices[j], invoices[i]];
        return { ...f, invoices };
    });
    const setPin = (paymentId) => (e) => {
        const value = e.target.value;
        setForm((f) => ({ ...f, pins: { ...f.pins, [paymentId]: value === IN_ORDER ? IN_ORDER : Number(value) } }));
    };

    const handleSubmit = async () => {
        setSaving(true);
        setDialogError(null);
        try {
            if (dialog.mode === 'delete') {
                await deleteCommissionCase(dialog.kase.case_number);
            } else {
                const indexByKey = new Map(form.invoices.map((inv, i) => [inv.key, i]));
                // Only pins that changed, so a save that doesn't touch
                // payments never needs them.
                const pins = Object.entries(form.pins)
                    .filter(([id, key]) => key !== (dialog.pins || {})[id])
                    .map(([id, key]) => ({
                        payment_id: Number(id),
                        invoice: key === IN_ORDER || !indexByKey.has(key) ? null : indexByKey.get(key),
                    }));
                const body = {
                    case_number: form.caseNumber.trim(),
                    notes: form.notes,
                    invoices: form.invoices.map((inv) => ({
                        id: inv.id,
                        label: inv.label,
                        commissionable_amount: parseAmount(inv.amount),
                        officer_commission_pct: Number(inv.officerRate),
                        splits: inv.agents.map((a) => ({
                            ...openerBody(a),
                            split_pct: Number(a.pct),
                            commission_pct: Number(a.rate),
                        })),
                    })),
                    pins,
                };
                if (dialog.mode === 'create') await createCommissionCase(body);
                else await updateCommissionCase(dialog.kase.case_number, body);
            }
            setDialog(null);
            await load();
        } catch (err) {
            setDialogError(err.message);
        } finally {
            setSaving(false);
        }
    };

    // Active agents, plus the case's own agents when editing one who has
    // since been marked inactive, so the picker never shows a blank. App users
    // who aren't agents yet are offered too; saving makes them one.
    const openerOptions = useMemo(() => {
        const current = new Set((dialog?.kase?.invoices || []).flatMap((inv) => inv.splits.map((s) => s.opener_id)));
        return openers.filter((o) => o.active || current.has(o.id));
    }, [openers, dialog]);
    const userOptions = useMemo(() => users.filter((u) => u.active && !u.opener_id), [users]);

    const personName = (agent) => {
        if (agent.opener === NEW_OPENER) return agent.newOpener.trim() || 'New agent';
        const [kind, id] = agent.opener.split(':');
        const list = kind === 'u' ? users : openers;
        const found = list.find((x) => String(x.id) === id);
        return found ? found.name : 'Split agent';
    };

    const invoiceValid = (inv) => {
        const amount = parseAmount(inv.amount);
        return amount > 0
            && Number(inv.officerRate) > 0 && Number(inv.officerRate) <= 100
            && totalPct(inv.agents) <= 100
            && inv.agents.every((a) => (a.opener === NEW_OPENER ? a.newOpener.trim() : a.opener)
                && splitCaps(a.pct, a.rate, amount) != null);
    };
    const formValid = form != null
        && /^\d{1,12}$/.test(form.caseNumber.trim())
        && form.invoices.length > 0
        && form.invoices.every(invoiceValid);

    const editRow = useCallback((row) => openEdit(row.kase || row), [openEdit]);
    const deleteRow = useCallback((row) => openDelete(row.kase || row), [openDelete]);

    const caseColumns = useMemo(() => [
        caseColumn(),
        { field: 'client_name', headerName: 'Client', minWidth: 160 },
        {
            colId: 'invoices',
            headerName: 'Invoices',
            minWidth: 200,
            valueGetter: (p) => (p.data && p.data.invoices
                ? p.data.invoices.map((inv) => `${inv.label || `Invoice ${inv.position}`} ${formatMoney(inv.commissionable_amount)}`).join(', ')
                : ''),
        },
        moneyColumn('commissionable_amount', 'Case Commissionable'),
        {
            colId: 'agents',
            headerName: 'Split Agents',
            minWidth: 200,
            valueGetter: (p) => {
                if (!p.data || !p.data.invoices) return '';
                const many = p.data.invoices.length > 1;
                const names = p.data.invoices.flatMap((inv) => inv.splits.map((s) => (
                    `${agentLabel(s)} ${formatPct(s.split_pct)}${many ? ` (inv. ${inv.position})` : ''}`
                )));
                return names.join(', ') || '—';
            },
        },
        {
            colId: 'officer_pct',
            headerName: 'Officer Share',
            headerTooltip: 'What the split agents leave on each invoice, earned by the settlement officer on each payment',
            width: 130,
            valueGetter: (p) => (p.data && p.data.invoices
                ? p.data.invoices.map((inv) => formatPct(inv.officer_pct)).join(' / ')
                : ''),
        },
        {
            colId: 'officers',
            headerName: 'Settlement Officer',
            minWidth: 170,
            valueGetter: (p) => (p.data && p.data.officers ? p.data.officers.join(', ') || '—' : ''),
        },
        moneyColumn('net_paid', 'Client Paid'),
        moneyColumn('agents_earned', 'Agents Earned'),
        moneyColumn('officer_earned', 'Officer Earned'),
        moneyColumn('earned', 'Total Earned'),
        { ...moneyColumn('cap', 'Max Commission'), headerTooltip: 'Everyone\'s commission once the client has paid every invoice' },
        { colId: 'progress', headerName: 'Progress', width: 150, cellRenderer: EarnedProgress, sortable: false, filter: false },
        { field: 'payments', headerName: 'Payments', type: 'rightAligned', width: 110, filter: 'agNumberColumnFilter' },
        { field: 'notes', headerName: 'Notes', minWidth: 160 },
        { field: 'created_at', headerName: 'Added', width: 120, valueFormatter: (p) => formatDate(p.value) },
        actionsColumn(editRow, deleteRow),
    ], [editRow, deleteRow]);

    const personColumns = useMemo(() => [
        caseColumn(),
        { field: 'client_name', headerName: 'Client', minWidth: 160 },
        { field: 'invoice', headerName: 'Invoice', minWidth: 160 },
        moneyColumn('invoice_amount', 'Invoice Commissionable'),
        { field: 'person', headerName: 'Person', minWidth: 170 },
        { field: 'role', headerName: 'Role', width: 150 },
        { field: 'share', headerName: 'Share', headerTooltip: 'Their percentage of each payment, up to the invoice\'s commissionable amount', type: 'rightAligned', width: 90, filter: 'agNumberColumnFilter', valueFormatter: (p) => formatPct(p.value) },
        { ...moneyColumn('commissionable_cap', 'Their Commissionable'), headerTooltip: 'Share % of the invoice\'s commissionable amount' },
        { field: 'rate', headerName: 'Rate', headerTooltip: 'Commission rate on their commissionable', type: 'rightAligned', width: 90, filter: 'agNumberColumnFilter', valueFormatter: (p) => formatPct(p.value) },
        moneyColumn('cap', 'Max Commission'),
        { ...moneyColumn('commissionable', 'Commissionable So Far'), headerTooltip: 'Their share of what the client has paid toward the invoice, up to its amount' },
        moneyColumn('earned', 'Earned'),
        { ...moneyColumn('remaining', 'Left to Earn'), valueFormatter: (p) => (p.value == null ? '' : formatMoney(p.value)) },
        { ...moneyColumn('forfeited', 'Forfeited'), headerTooltip: 'What payments after their last day would have earned them' },
        { colId: 'progress', headerName: 'Progress', width: 150, cellRenderer: EarnedProgress, sortable: false, filter: false },
        actionsColumn(editRow, deleteRow),
    ], [editRow, deleteRow]);

    const rows = useMemo(() => (view === 'case' ? cases : personRows(cases)), [view, cases]);

    const defaultColDef = useMemo(() => ({ sortable: true, filter: true, resizable: true, minWidth: 90 }), []);

    // The saved case's payments, for moving them between invoices.
    const paymentList = dialog?.mode === 'edit' ? dialog.kase.payment_list : [];
    const savedInvoiceName = (id) => {
        const inv = dialog?.kase?.invoices.find((x) => x.id === id);
        return inv ? invoiceName(inv.position, inv.label) : 'an invoice';
    };

    const renderAgent = (inv, a, amount) => {
        const caps = splitCaps(a.pct, a.rate, amount);
        return (
            <div key={a.key} className="com__agent">
                <div className="com__agent-fields">
                    <label className="mkt__field com__agent-person">
                        <span>Agent</span>
                        <select value={a.opener} onChange={setAgentField(inv.key, a.key, 'opener')}>
                            <option value="">Pick an agent…</option>
                            {openerOptions.length > 0 && (
                                <optgroup label="Agents">
                                    {openerOptions.map((o) => (
                                        <option key={o.id} value={`o:${o.id}`}>
                                            {o.name}{o.active ? '' : ' (inactive)'}
                                        </option>
                                    ))}
                                </optgroup>
                            )}
                            {userOptions.length > 0 && (
                                <optgroup label="App users (added as an agent on save)">
                                    {userOptions.map((u) => (
                                        <option key={u.id} value={`u:${u.id}`}>{u.name}</option>
                                    ))}
                                </optgroup>
                            )}
                            <option value={NEW_OPENER}>+ New agent…</option>
                        </select>
                    </label>
                    <label className="mkt__field com__agent-pct">
                        <span>Split %</span>
                        <input type="number" min="0.01" max="100" step="0.01" value={a.pct} onChange={setAgentField(inv.key, a.key, 'pct')} />
                    </label>
                    <label className="mkt__field com__agent-pct">
                        <span>Rate %</span>
                        <input type="number" min="0.01" max="100" step="0.01" value={a.rate} onChange={setAgentField(inv.key, a.key, 'rate')} />
                    </label>
                    <button
                        type="button"
                        className="mkt__row-btn mkt__row-btn--danger com__agent-remove"
                        onClick={() => removeAgent(inv.key, a.key)}
                        aria-label="Remove this split agent"
                    >
                        Remove
                    </button>
                </div>
                {a.opener === NEW_OPENER && (
                    <label className="mkt__field">
                        <span>New Agent's Name</span>
                        <input
                            type="text"
                            value={a.newOpener}
                            onChange={setAgentField(inv.key, a.key, 'newOpener')}
                            placeholder="e.g. Sam Smith"
                            autoFocus={!a.newOpener}
                        />
                    </label>
                )}
                {caps != null && (
                    <span className="com__hint">
                        {formatPct(a.pct)} of {formatMoney(amount)} = {formatMoney(caps.commissionable)} commissionable;
                        {' '}up to {formatMoney(caps.commission)} commission at {formatPct(a.rate)}.
                    </span>
                )}
            </div>
        );
    };

    const renderInvoice = (inv, i) => {
        const amount = parseAmount(inv.amount);
        const agentsPct = totalPct(inv.agents);
        const officerPct = Math.max(0, Math.round((100 - agentsPct) * 100) / 100);
        const officerCaps = officerPct > 0 ? splitCaps(officerPct, inv.officerRate, amount) : null;
        const many = form.invoices.length > 1;
        return (
            <fieldset key={inv.key} className="com__invoice">
                <legend className="com__invoice-title">
                    {invoiceName(i + 1, inv.label.trim())}
                </legend>
                {many && (
                    <div className="com__invoice-actions">
                        <button type="button" className="mkt__row-btn" onClick={() => moveInvoice(inv.key, -1)} disabled={i === 0}>↑ Earlier</button>
                        <button type="button" className="mkt__row-btn" onClick={() => moveInvoice(inv.key, 1)} disabled={i === form.invoices.length - 1}>↓ Later</button>
                        <button type="button" className="mkt__row-btn mkt__row-btn--danger" onClick={() => removeInvoice(inv.key)}>Remove invoice</button>
                    </div>
                )}
                <div className="com__form-row">
                    <label className="mkt__field">
                        <span>Name (optional)</span>
                        <input type="text" value={inv.label} onChange={setInvoiceField(inv.key, 'label')} placeholder="e.g. Initial, Additional Services" />
                    </label>
                    <label className="mkt__field">
                        <span>Commissionable Amount</span>
                        <input
                            type="text"
                            inputMode="decimal"
                            value={inv.amount}
                            onChange={setInvoiceField(inv.key, 'amount')}
                            placeholder="e.g. 495.00"
                        />
                    </label>
                </div>

                <h4 className="com__subsection-title">Split agents</h4>
                {inv.agents.length === 0 && (
                    <p className="com__hint">None: the settlement officer gets all of this invoice.</p>
                )}
                {inv.agents.map((a) => renderAgent(inv, a, amount))}
                <button type="button" className="com__link-btn" onClick={() => addAgent(inv.key)}>+ Add split agent</button>

                <div className="com__form-row" style={{ marginTop: 12 }}>
                    <label className="mkt__field">
                        <span>Settlement Officer Share</span>
                        <input type="text" value={formatPct(officerPct)} readOnly disabled />
                    </label>
                    <label className="mkt__field">
                        <span>Officer Rate %</span>
                        <input type="number" min="0.01" max="100" step="0.01" value={inv.officerRate} onChange={setInvoiceField(inv.key, 'officerRate')} />
                    </label>
                </div>

                {agentsPct > 100 ? (
                    <div className="mkt__preview mkt__preview--warn">
                        The split agents add up to {formatPct(agentsPct)}. Together they can't take more than 100%.
                    </div>
                ) : amount > 0 && (
                    <div className="mkt__preview">
                        <span>
                            Of {formatMoney(amount)}:{' '}
                            {inv.agents.map((a) => {
                                const caps = splitCaps(a.pct, a.rate, amount);
                                return caps && (
                                    <React.Fragment key={a.key}>
                                        <strong>{personName(a)}</strong> {formatMoney(caps.commissionable)} commissionable,
                                        up to {formatMoney(caps.commission)};{' '}
                                    </React.Fragment>
                                );
                            })}
                            <strong>settlement officer</strong>{' '}
                            {officerPct === 0 && 'nothing.'}
                            {officerCaps && (
                                <>{formatMoney(officerCaps.commissionable)} commissionable, up to {formatMoney(officerCaps.commission)}.</>
                            )}
                        </span>
                    </div>
                )}
            </fieldset>
        );
    };

    return (
        <div className="page">
            <div className="mkt__header">
                <div>
                    <h1 className="page__title">Commission Splits</h1>
                    <p className="page__subtitle">
                        Each case has one or more invoices, each with a commissionable amount shared by its split agents;
                        the settlement officer on each payment gets the rest. Payments fill a case's invoices in order.
                        Earned amounts update when a payment report is imported.
                    </p>
                </div>
                <div className="mkt__header-actions">
                    <button className="blueButton" onClick={() => openCreate()}>+ Add Case</button>
                </div>
            </div>

            {error && <p className="mkt__error" style={{ marginTop: 16 }}>{error}</p>}
            {loading && <p className="page__subtitle">Loading cases…</p>}

            {!loading && !error && cases.length === 0 && (
                <p className="page__subtitle" style={{ marginTop: 16 }}>
                    No cases yet. Add one when a case closes, with its invoices and split agents.
                </p>
            )}

            {cases.length > 0 && (
                <div style={{ marginTop: 16 }}>
                    <div className="mkt__controls">
                        <div className="mkt__segmented" role="group" aria-label="Show">
                            <button type="button" className={view === 'case' ? 'mkt__segmented--on' : ''} onClick={() => setView('case')}>
                                By case
                            </button>
                            <button type="button" className={view === 'person' ? 'mkt__segmented--on' : ''} onClick={() => setView('person')}>
                                By person
                            </button>
                        </div>
                    </div>
                    <DataGrid
                        key={view}
                        rowData={rows}
                        columnDefs={view === 'case' ? caseColumns : personColumns}
                        defaultColDef={defaultColDef}
                        height="calc(100vh - 310px)"
                        enableExport
                        exportFileName={() => `Commission_Splits_${view === 'case' ? 'by_case' : 'by_person'}_${toISODate(new Date())}`}
                    />
                </div>
            )}

            {dialog && dialog.mode !== 'delete' && form && (
                <Modal
                    title={dialog.mode === 'create' ? 'Add Case' : `Edit Case ${dialog.kase.case_number}`}
                    onClose={closeDialog}
                    onSubmit={handleSubmit}
                    submitLabel={dialog.mode === 'create' ? 'Add Case' : 'Save Changes'}
                    submitting={saving}
                    submitDisabled={!formValid}
                    error={dialogError}
                    wide
                >
                    <label className="mkt__field">
                        <span>Case Number</span>
                        <input
                            type="text"
                            inputMode="numeric"
                            value={form.caseNumber}
                            onChange={setField('caseNumber')}
                            placeholder="e.g. 37734"
                            autoFocus={!form.caseNumber}
                        />
                    </label>

                    <div>
                        <h3 className="com__section-title">Invoices</h3>
                        <p className="com__hint">
                            The commissionable amount is usually the invoice total; payments past it earn nobody anything.
                            Payments fill the invoices in this order: the first until it's paid off, then the next.
                        </p>
                        {form.invoices.map(renderInvoice)}
                        <button type="button" className="com__link-btn" onClick={addInvoice}>+ Add invoice</button>
                    </div>

                    {paymentList.length > 0 && form.invoices.length > 1 && (
                        <div>
                            <h3 className="com__section-title">Payments</h3>
                            <p className="com__hint">
                                Where each payment counted as of the last save. If a payment belongs to a different
                                invoice (the client paid two at once), pick it here; the rest keep filling in order.
                            </p>
                            <div className="com__table-wrap">
                                <table className="com__table">
                                    <thead>
                                        <tr>
                                            <th>Paid</th>
                                            <th className="com__num">Amount</th>
                                            <th>Officer</th>
                                            <th>Counted toward</th>
                                            <th>Invoice</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paymentList.map((p) => (
                                            <tr key={p.id}>
                                                <td>{formatPaidAt(p.paid_at)}</td>
                                                <td className="com__num">{formatMoney(p.amount)}</td>
                                                <td>{p.settlement_officer || '—'}</td>
                                                <td>
                                                    {(p.allocations || []).map((a) => (
                                                        p.allocations.length > 1
                                                            ? `${savedInvoiceName(a.invoice_id)} ${formatMoney(a.amount)}`
                                                            : savedInvoiceName(a.invoice_id)
                                                    )).join(', ')}
                                                </td>
                                                <td>
                                                    <select value={form.pins[p.id] ?? IN_ORDER} onChange={setPin(p.id)}>
                                                        <option value={IN_ORDER}>In order (automatic)</option>
                                                        {form.invoices.map((inv, i) => (
                                                            <option key={inv.key} value={inv.key}>{invoiceName(i + 1, inv.label.trim())}</option>
                                                        ))}
                                                    </select>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    <label className="mkt__field">
                        <span>Notes</span>
                        <textarea className="mkt__textarea" value={form.notes} onChange={setField('notes')} />
                    </label>
                </Modal>
            )}

            {dialog && dialog.mode === 'delete' && (
                <Modal
                    title="Delete Case"
                    onClose={closeDialog}
                    onSubmit={handleSubmit}
                    submitLabel="Delete Case"
                    submitVariant="danger"
                    submitting={saving}
                    error={dialogError}
                >
                    <p className="mkt-modal__text">
                        Delete case <strong>{dialog.kase.case_number}</strong> with its
                        {' '}{dialog.kase.invoices.length === 1 ? 'invoice' : `${dialog.kase.invoices.length} invoices`} and
                        split agents? Their commission on it will no longer be counted, and the settlement officer goes
                        back to earning on every payment with no cap. The imported payments stay.
                    </p>
                </Modal>
            )}
        </div>
    );
}
